import React, { useCallback, useEffect } from "react";
import { FormattedNumber } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { useGetV4WarehousesQuery, usePutV4ManufacturerByRouteManufacturerIdInvoiceAndReceiptNumberMutation } from "randmar-api-client";
import { Alert, AlertDescription, AlertTitle, Badge, Button, Card, Separator, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, useToast } from "randmarcomps";
import { CalendarDays, Info, MapPin, ReceiptText } from "lucide-react";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Receipt } from "../../stores/document/Document";
import CommentsView from "../comments/CommentsView";
import ProductDetailsModal from "../products/ProductDetailsModal";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import InvoicePanel from "../util/InvoicePanel";
import LoadingComponent from "../util/LoadingComponent";
import TotalTable from "../util/TotalTable";

interface IProps {
  receipt: Receipt;
}

const ReceiptView: React.FC<IProps> = ({ receipt }) => {
  const { applicationId } = useManufacturer();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [insertInvoice, { isLoading }] = usePutV4ManufacturerByRouteManufacturerIdInvoiceAndReceiptNumberMutation();
  const { data: warehouses } = useGetV4WarehousesQuery();


  // The original component had navigation logic inside the render method.
  // This is a side effect and should be handled by useEffect in a functional component.
  useEffect(() => {
    if (!receipt.partNumbers) {
      navigate(`/${applicationId}/404`);
    }
  }, [receipt.partNumbers, applicationId, navigate]);

  const getWarehouseNameFromCode = useCallback((code?: string) => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const handleConfirm = async (InvoiceNumber: string, InvoiceDate: string, InvoiceTotal: number) => {
    try {
      await insertInvoice({
        routeManufacturerId: applicationId,
        receiptNumber: receipt.receiptNumber || "",
        invoiceToReceiptInput: {
          InvoiceNumber,
          InvoiceDate,
          InvoiceTotal,
        }
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Receipt successfully invoiced." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not invoice this receipt" });
    }
  };

  if (isLoading) {
    return <LoadingComponent text="Awaiting response" />;
  }

  // If the receipt has no parts, we are navigating away, so render nothing.
  if (!receipt.partNumbers) {
    return null;
  }

  return (
    <>
      <section className="space-y-6" aria-label="Receipt details">
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reception</p>
                  <h2 className="text-[24px] font-semibold text-slate-900">Overview</h2>
                </div>
                <Badge variant={receipt.invoiced ? "success" : "warning"} size="sm">
                  {receipt.invoiced ? "Invoiced" : "Uninvoiced"}
                </Badge>
              </header>

              <Alert className="rounded-2xl border border-sky-100 bg-sky-50 shadow-sm">
                <Info className="h-4 w-4 text-sky-600" aria-hidden="true" />
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <AlertTitle className="text-slate-900">Reception invoicing</AlertTitle>
                    <AlertDescription className="text-slate-700">
                      {receipt.invoiceNumber
                        ? <>Invoice #: <span className="font-semibold">{receipt.invoiceNumber}</span></>
                        : "This reception has not been invoiced yet."}
                    </AlertDescription>
                  </div>
                  {!receipt.invoiced ? (
                    <InvoicePanel name="Add Invoice" onConfirm={handleConfirm} />
                  ) : null}
                </div>
              </Alert>

              <dl className="grid gap-2 sm:grid-cols-2" aria-label="Reception metadata">
                <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
                  <CalendarDays className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reception date</dt>
                    <dd className="text-[15px] text-slate-700">{FormatDateFromInt(receipt.receiptDate)}</dd>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
                  <MapPin className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Location</dt>
                    <dd className="text-[15px] text-slate-700">{getWarehouseNameFromCode(receipt.location)}</dd>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50 sm:col-span-2">
                  <ReceiptText className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Purchase order</dt>
                    <dd className="text-[15px] text-slate-700">
                      {receipt.purchaseOrderNumber ? (
                        <Link
                          to={Path.document(applicationId, receipt.purchaseOrderNumber)}
                          className="font-semibold text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                        >
                          {receipt.purchaseOrderNumber}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                </div>
              </dl>
            </div>
          </Card>

          <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <header className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">History</p>
                <h2 className="text-[24px] font-semibold text-slate-900">Comments & totals</h2>
              </header>
              <CommentsView comments={receipt.comments || ""} />
              <Separator />
              <div className="space-y-2">
                <h3 className="text-[20px] font-medium text-slate-900">Accounting</h3>
                <TotalTable
                  subTotal={receipt.subTotal || 0}
                  taxAmount1={receipt.taxAmount1 || 0}
                  taxAmount2={receipt.taxAmount2 || 0}
                  total={receipt.total || 0}
                />
              </div>
            </div>
          </Card>
        </div>

        <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-[20px] font-medium text-slate-900">Part numbers received</h3>
              <p className="text-[15px] text-slate-600">Line items received on this reception.</p>
            </div>
            <p className="text-sm text-slate-500">
              {(receipt.partNumbers ?? []).length} line{(receipt.partNumbers ?? []).length === 1 ? "" : "s"}
            </p>
          </div>

          <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow className="h-14 hover:bg-gray-50">
                  <TableHead className="min-w-[240px] text-xs font-semibold uppercase tracking-wide text-slate-500">Product</TableHead>
                  <TableHead className="w-[140px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</TableHead>
                  <TableHead className="w-[160px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Unit price</TableHead>
                  <TableHead className="w-[120px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(receipt.partNumbers ?? [])
                  .slice()
                  .sort((a, b) => ((a.mpn || "").localeCompare(b.mpn || "")))
                  .map(pn => (
                    <TableRow key={`receipt-part-${pn.mpn ?? pn.randmarSKU ?? "unknown"}`} className="h-14 border-b border-gray-100 hover:bg-gray-50/60">
                      <TableCell>
                        <div className="flex flex-col">
                          <Link
                            to={Path.product(applicationId, pn.randmarSKU)}
                            className="font-semibold text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                          >
                            {pn.mpn}
                          </Link>
                          <span className="text-sm text-slate-500">{pn.description}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-slate-700">{pn.quantity?.toLocaleString("en-US") ?? "—"}</TableCell>
                      <TableCell className="text-center text-slate-700"><FormattedNumber {...CurrencyUtils.format} value={pn.unitPrice || 0} /></TableCell>
                      <TableCell className="text-center"><ProductDetailsModal randmarSKU={pn.randmarSKU || ""} /></TableCell>
                    </TableRow>
                  ))}
                {!receipt.partNumbers?.length ? (
                  <TableRow className="h-14">
                    <TableCell colSpan={4} className="text-center text-sm text-slate-500">
                      This receipt has no part numbers.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </Card>

        {receipt.purchaseOrderNumber ? (
          <div className="flex justify-end">
            <Link to={Path.document(applicationId, receipt.purchaseOrderNumber)} className="w-full sm:w-auto">
              <Button variant="outline" className="w-full justify-center rounded-xl sm:w-auto">
                View purchase order
              </Button>
            </Link>
          </div>
        ) : null}
      </section>
    </>
  );
};

export default ReceiptView;
